import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { logout, getUserRole } from '../../services/auth';
import { toast } from 'react-toastify';

const Navigation = () => {
    const [user, setUser] = useState(null);
    const [userRole, setUserRole] = useState(null);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [hoveredMenuItem, setHoveredMenuItem] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const userData = localStorage.getItem('user');
        const role = getUserRole();
        if (userData) {
            setUser(JSON.parse(userData));
            setUserRole(role);
        }
    }, []);

    const handleLogout = async () => {
        await logout();
        toast.success('Logged out successfully');
        navigate('/login');
    };

    const closeSidebar = () => setSidebarOpen(false);
    const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
    const toggleDropdown = () => setDropdownOpen(!dropdownOpen);

    // ----- inline styles -----
    const sidebarStyle = {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '260px',
        height: '100vh',
        background: 'linear-gradient(180deg, #0b2b3b 0%, #0a1c28 100%)',
        color: '#d1e3ed',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1040,
        transition: 'transform 0.3s ease',
        overflowY: 'auto',
        boxShadow: '2px 0 10px rgba(0,0,0,0.1)',
        transform: sidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
    };

    // On larger screens, sidebar is always visible
    // We need to add a media query override using a style tag or inline via JS.
    // Since we cannot use @media inline, we'll use a <style> tag in the component.
    // We'll inject CSS for desktop to show sidebar by default.
    // Alternatively, we can use a useEffect to add/remove class based on window width.
    // For simplicity, we'll keep the current approach: sidebar hidden on mobile by default, but on desktop we'll show it via CSS.
    // We'll add a style tag inside the component.

    const brandStyle = {
        padding: '20px 20px 15px',
        borderBottom: '1px solid rgba(255,255,255,0.05)',
        flexShrink: 0,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    };

    const brandLinkStyle = {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        textDecoration: 'none',
        color: 'white',
        fontWeight: 700,
        fontSize: '1.2rem',
    };

    const brandImgStyle = {
        height: '36px',
        width: 'auto',
    };

    const closeBtnStyle = {
        background: 'transparent',
        border: 'none',
        color: '#d1e3ed',
        fontSize: '1.2rem',
        cursor: 'pointer',
        display: 'block', // visible on mobile; we'll hide on desktop via media query
    };

    const navStyle = {
        flex: 1,
        padding: '15px 0',
        overflowY: 'auto',
    };

    const menuStyle = {
        listStyle: 'none',
        padding: 0,
        margin: 0,
    };

    const menuItemStyle = {
        margin: '2px 8px',
    };

    const getMenuItemLinkStyle = (isActive, isHovered) => ({
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '10px 14px',
        borderRadius: '8px',
        color: isActive ? 'white' : '#b0c8d6',
        textDecoration: 'none',
        fontSize: '0.85rem',
        fontWeight: 500,
        transition: 'background 0.2s, color 0.2s',
        cursor: 'pointer',
        border: 'none',
        background: isActive ? '#2c7da0' : (isHovered ? 'rgba(255,255,255,0.08)' : 'transparent'),
        width: '100%',
        textAlign: 'left',
    });

    const iconStyle = {
        width: '20px',
        fontSize: '1rem',
    };

    const footerStyle = {
        padding: '15px 20px',
        borderTop: '1px solid rgba(255,255,255,0.05)',
        flexShrink: 0,
    };

    const userBtnStyle = {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        background: 'transparent',
        border: 'none',
        color: 'white',
        width: '100%',
        padding: '8px 10px',
        borderRadius: '8px',
        transition: 'background 0.2s',
        fontSize: '0.85rem',
        cursor: 'pointer',
    };

    const userBtnHoverStyle = {
        background: 'rgba(255,255,255,0.08)',
    };

    const avatarStyle = {
        width: '32px',
        height: '32px',
        background: 'rgba(255,255,255,0.15)',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    };

    const userNameStyle = {
        flex: 1,
        fontWeight: 500,
        textAlign: 'left',
    };

    const dropdownMenuStyle = {
        position: 'absolute',
        bottom: '100%', // show above the button
        left: 0,
        right: 0,
        background: 'white',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        padding: '8px 0',
        display: dropdownOpen ? 'block' : 'none',
        minWidth: '200px',
        marginBottom: '4px',
    };

    const dropdownItemStyle = {
        display: 'block',
        padding: '8px 16px',
        color: '#1f2937',
        textDecoration: 'none',
        fontSize: '0.85rem',
        transition: 'background 0.15s',
        border: 'none',
        background: 'transparent',
        width: '100%',
        textAlign: 'left',
        cursor: 'pointer',
    };

    const dropdownItemHoverStyle = {
        background: '#f1f5f9',
    };

    const dividerStyle = {
        margin: '4px 0',
        border: 'none',
        borderTop: '1px solid #e2e8f0',
    };

    // Toggle button (hamburger) for mobile
    const toggleBtnStyle = {
        display: 'none', // hidden on desktop, shown on mobile via media query
        position: 'fixed',
        top: '15px',
        left: '15px',
        zIndex: 1050,
        background: '#0b2b3b',
        border: 'none',
        color: 'white',
        padding: '8px 12px',
        borderRadius: '6px',
        fontSize: '1.2rem',
        cursor: 'pointer',
    };

    // Overlay for mobile
    const overlayStyle = {
        display: sidebarOpen ? 'block' : 'none',
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: 'rgba(0,0,0,0.5)',
        zIndex: 1030,
    };

    // We'll inject a <style> tag for desktop media query to show sidebar by default
    const desktopStyles = `
        @media (min-width: 992px) {
            .nav-sidebar {
                transform: translateX(0) !important;
            }
            .nav-close-btn {
                display: none !important;
            }
            .nav-toggle-btn {
                display: none !important;
            }
            .nav-overlay {
                display: none !important;
            }
            .main-content-offset {
                margin-left: 260px;
            }
        }
        @media (max-width: 991px) {
            .nav-sidebar {
                transform: translateX(-100%);
            }
            .nav-sidebar.open {
                transform: translateX(0);
            }
            .nav-close-btn {
                display: block !important;
            }
            .nav-toggle-btn {
                display: block !important;
            }
            .nav-overlay {
                display: block;
            }
            .main-content-offset {
                margin-left: 0;
            }
        }
        /* Make sure sidebar is visible on desktop */
        .nav-sidebar {
            transform: translateX(0);
        }
    `;

    // Determine active link
    const isActiveLink = (path) => location.pathname === path;

    return (
        <>
            <style>{desktopStyles}</style>

            {/* Toggle button */}
            <button
                className="nav-toggle-btn"
                style={toggleBtnStyle}
                onClick={toggleSidebar}
                aria-label="Toggle sidebar"
            >
                <i className="fas fa-bars"></i>
            </button>

            {/* Overlay */}
            {sidebarOpen && <div className="nav-overlay" style={overlayStyle} onClick={closeSidebar}></div>}

            {/* Sidebar */}
            <aside className={`nav-sidebar ${sidebarOpen ? 'open' : ''}`} style={sidebarStyle}>
                {/* Brand */}
                <div style={brandStyle}>
                    <Link to="/admin/dashboard" style={brandLinkStyle} onClick={closeSidebar}>

                        <span>Admin</span>
                    </Link>
                    <button className="nav-close-btn" style={closeBtnStyle} onClick={closeSidebar}>
                        <i className="fas fa-times"></i>
                    </button>
                </div>

                {/* Navigation */}
                <nav style={navStyle}>
                    <ul style={menuStyle}>
                        {/* Dashboard */}
                        <li style={menuItemStyle}>
                            <Link
                                to="/admin/dashboard"
                                style={getMenuItemLinkStyle(isActiveLink('/admin/dashboard'), hoveredMenuItem === 'dashboard')}
                                onClick={closeSidebar}
                                onMouseEnter={() => setHoveredMenuItem('dashboard')}
                                onMouseLeave={() => setHoveredMenuItem(null)}
                            >
                                <i className="fas fa-tachometer-alt" style={iconStyle}></i>
                                <span>Dashboard</span>
                            </Link>
                        </li>

                        {/* Photo Gallery */}
                        <li style={menuItemStyle}>
                            <Link
                                to="/admin/photos"
                                style={getMenuItemLinkStyle(isActiveLink('/admin/photos'), hoveredMenuItem === 'photos')}
                                onClick={closeSidebar}
                                onMouseEnter={() => setHoveredMenuItem('photos')}
                                onMouseLeave={() => setHoveredMenuItem(null)}
                            >
                                <i className="fas fa-images" style={iconStyle}></i>
                                <span>Photo Gallery</span>
                            </Link>
                        </li>

                        {/* Video Gallery */}
                        <li style={menuItemStyle}>
                            <Link
                                to="/admin/videos"
                                style={getMenuItemLinkStyle(isActiveLink('/admin/videos'), hoveredMenuItem === 'videos')}
                                onClick={closeSidebar}
                                onMouseEnter={() => setHoveredMenuItem('videos')}
                                onMouseLeave={() => setHoveredMenuItem(null)}
                            >
                                <i className="fas fa-video" style={iconStyle}></i>
                                <span>Video Gallery</span>
                            </Link>
                        </li>
                        <li style={menuItemStyle}>
                            <Link
                                to="/admin/contacts"
                                style={getMenuItemLinkStyle(isActiveLink('/admin/contacts'), hoveredMenuItem === 'contacts')}
                                onClick={closeSidebar}
                                onMouseEnter={() => setHoveredMenuItem('contacts')}
                                onMouseLeave={() => setHoveredMenuItem(null)}
                            >
                                <i className="fas fa-phone" style={iconStyle}></i>
                                <span>Contacts</span>
                            </Link>
                        </li>
                        <li style={menuItemStyle}>
                            <Link
                                to="/admin/donations"
                                style={getMenuItemLinkStyle(isActiveLink('/admin/donations'), hoveredMenuItem === 'donations')}
                                onClick={closeSidebar}
                                onMouseEnter={() => setHoveredMenuItem('donations')}
                                onMouseLeave={() => setHoveredMenuItem(null)}
                            >
                                <i className="fas fa-dollar" style={iconStyle}></i>
                                <span>Donations</span>
                            </Link>
                        </li>
                    </ul>
                </nav>

                {/* Footer / User area */}
                <div style={footerStyle}>
                    <div style={{ position: 'relative' }}>
                        <button
                            style={userBtnStyle}
                            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                            onClick={toggleDropdown}
                        >
                            <div style={avatarStyle}>
                                <i className="fas fa-user-circle"></i>
                            </div>
                            <span style={userNameStyle}>{user?.name || 'Staff'}</span>
                            <i className="fas fa-chevron-down fa-xs"></i>
                        </button>
                        {/* Dropdown menu */}
                        <div style={dropdownMenuStyle}>
                            <Link to="/user/profile" style={dropdownItemStyle} onClick={() => { closeSidebar(); setDropdownOpen(false); }}>
                                <i className="fas fa-user me-2"></i>Profile
                            </Link>
                            <Link to="/user/change-password" style={dropdownItemStyle} onClick={() => { closeSidebar(); setDropdownOpen(false); }}>
                                <i className="fas fa-key me-2"></i>Change Password
                            </Link>
                            <hr style={dividerStyle} />
                            <button style={dropdownItemStyle} onClick={() => { handleLogout(); setDropdownOpen(false); }}>
                                <i className="fas fa-sign-out-alt me-2"></i>Logout
                            </button>
                        </div>
                    </div>
                </div>
            </aside>
        </>
    );
};

export default Navigation;